import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { baseAxios } from '../utils/axiosConfig';  // Changed to baseAxios which has withCredentials enabled
import './Login.css';
import logo from '../assets/images/IMT_logo.png';
import { useUserInfo } from '../contexts/UserContext';
import { dashboardService } from '../services/dashboardService';
import { authService } from '../services/authService';  // Import the new auth service
import { useFlashMessage } from './FlashMessage';
import { getErrorMessage } from '../utils/errorMessages';
import TestPeoplePanel from './TestPeoplePanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const history = useHistory();
  const { login, updateUser } = useContext(AuthContext);
  const { updateUserInfo } = useUserInfo();
  const { showSuccess, showError } = useFlashMessage();

  // Check if user is already authenticated and redirect to dashboard
  useEffect(() => {
    const checkAuth = async () => {
      if (authService.isLoggedIn()) {
        try {
          // Verify the session is still valid
          await dashboardService.getUserInfo();
          // If successful, redirect to dashboard
          history.push('/dashboard');
        } catch (err) {
          // Session invalid, clear localStorage and stay on login
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('user_id');
        }
      }
    };
    checkAuth();
  }, [history]);

  // What a successful sign-in does on this page, whichever way the person
  // signed in: the same stored keys, the same profile fetch, the same redirect.
  const finishSignIn = async (response) => {
    // The response now contains user data but not tokens
    // Tokens are stored in HttpOnly cookies
    if (response && response.user_id) {
      // Store minimal user info
      localStorage.setItem('user_id', response.user_id.toString());
      localStorage.setItem('isLoggedIn', 'true');

      // Show success message
      showSuccess('Login successful!');

      // Fetch user info after successful login
      try {
        const userInfoData = await dashboardService.getUserInfo();
        updateUserInfo(userInfoData);

        // Store full user info in AuthContext including access flags
        login({
          id: response.user_id,
          personio_id: response.personio_id,
          email: response.email,
          has_full_access: userInfoData.has_full_access,
          is_superuser: userInfoData.is_superuser,
          is_staff: userInfoData.is_staff,
          is_crm_approver: userInfoData.is_crm_approver,
          is_financial_approver: userInfoData.is_financial_approver,
          is_delivery_reviewer: userInfoData.is_delivery_reviewer
        });
      } catch (userInfoError) {
        console.error('Error fetching user info:', userInfoError);
        showError('Warning: Could not load user profile data');

        // Login with minimal info if user info fetch fails
        login({
          id: response.user_id,
          personio_id: response.personio_id,
          email: response.email
        });
      }

      // Small delay to show success message before redirect
      setTimeout(() => {
        history.push('/dashboard');
      }, 500);
    } else {
      const errorMsg = 'Login failed. Please try again.';
      setError(errorMsg);
      showError(errorMsg);
    }
  };

  // One sign-in attempt, whichever way the person signs in: clear the old
  // error, show the spinner, then finish like any successful sign-in or say
  // why it failed.
  const attemptSignIn = async (signIn, failureMessage) => {
    setError('');
    setIsLoading(true);

    try {
      const response = await signIn();
      await finishSignIn(response);
    } catch (err) {
      console.error('Login error:', err);
      const errorMsg = getErrorMessage(err, failureMessage);
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    return attemptSignIn(
      () => authService.login({
        email: email,
        password: password
      }),
      'Login failed. Please check your credentials and try again.'
    );
  };

  // Test system only: the panel below the form lists made-up test people;
  // on live it never appears (see TestPeoplePanel).
  const handleTestPerson = (key) => attemptSignIn(
    () => authService.signInAsTestPerson(key),
    'Could not sign in as that test person. Please try again.'
  );

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-branding">
          <img src={logo} alt="IMT" className="login-logo" />
        </div>
        {error && <p className="error-message">{error}</p>}
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div className="login-password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="login-password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <FontAwesomeIcon 
                icon={showPassword ? faEyeSlash : faEye} 
                className="login-eye-icon"
              />
            </button>
          </div>
          <button 
            className={`login-button ${isLoading ? 'loading' : ''}`} 
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Logging in...
              </>
            ) : (
              'log in'
            )}
          </button>
        </form>
        <TestPeoplePanel onPick={handleTestPerson} disabled={isLoading} />
      </div>
    </div>
  );
}

export default Login;